import { useState, useMemo, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import { FolderOpen, UploadCloud, LayoutGrid, List, FolderPlus } from "lucide-react";

import { AutofillCard } from "../components/AutofillCard";
import { AutofillReviewPrompt } from "../components/AutofillReviewPrompt";
import { Navbar } from "../components/Navbar";
import { StatsShelf } from "../components/StatsShelf";
import { CategoryFilterDock } from "../components/CategoryFilterDock";
import { CategoryManager } from "../components/CategoryManager";
import { DocumentGrid } from "../components/DocumentGrid";
import { DocumentTable } from "../components/DocumentTable";
import { DocumentInspector } from "../components/DocumentInspector";
import { UploadDropzoneModal } from "../components/UploadDropzoneModal";
import { GettingStarted } from "../components/GettingStarted";
import { OwnerChecklist } from "../components/onboarding/OwnerChecklist";
import { UploadFollowUp } from "../components/onboarding/UploadFollowUp";
import { ImportInsightsCard } from "../components/ImportInsightsCard";
import { VerifyEmailBanner } from "../components/EmptyVaultWelcome";
import { PendingReviewNudge } from "../components/vault/PendingReviewNudge";
import { NeedsVoiceNudge } from "../components/vault/NeedsVoiceNudge";
import { InstallPrompt } from "../components/InstallPrompt";
import { CapturePanel } from "../components/CapturePanel";
import { Tip } from "../components/Tip";
import { ForwardingHubModal } from "../components/ForwardingHubModal";
import { AskVaultWidget } from "../components/AskVaultWidget";
import { ConciergeBanner } from "../components/ConciergeBanner";
import { TrialBanner } from "../components/TrialBanner";
import { SigningDayCard } from "../components/SigningDayCard";
import { ReviewPrompt } from "../components/Reviews";
import { ExecutorModeCard } from "../components/ExecutorModeCard";
import { WelcomeCard } from "../components/WelcomeCard";
import { AllowanceNudge } from "../components/AllowanceNudge";
import { RecoveryEmailNudge } from "../components/RecoveryEmailNudge";
import { CameraCaptureModal } from "../components/CameraCaptureModal";
import { RecordLetterModal } from "../components/RecordLetterModal";
import { PasswordImportModal } from "../components/PasswordImportModal";
import { EmergencyKitModal } from "../components/EmergencyKitModal";
import { KeyRound, LifeBuoy, ShieldCheck, Compass, Send, Trash2 } from "lucide-react";
import { PasswordExportDialog } from "../components/BitwardenExportDialog";
import { TrashSheet } from "../components/TrashSheet";
import { useSecurity } from "../context/SecurityContext";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  fetchDocuments,
  fetchStats,
  fetchDocument,
  overrideCategory,
  deleteDocument,
} from "../lib/api";
import { getCategory } from "../lib/categories";
import { logError } from "../lib/log";
import { tr } from "../lib/i18n";

const SKELETONS = ["s1", "s2", "s3", "s4", "s5", "s6"];

const useDebounced = (value, delay = 350) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

const SORT_OPTIONS = [
  { value: "uploaded_at:desc", label: "Newest first" },
  { value: "uploaded_at:asc", label: "Oldest first" },
  { value: "title:asc", label: "Name (A–Z)" },
  { value: "title:desc", label: "Name (Z–A)" },
  { value: "confidence:desc", label: "Confidence (high)" },
  { value: "confidence:asc", label: "Confidence (low)" },
  { value: "category:asc", label: "Category" },
];

export default function Vault() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [followDocs, setFollowDocs] = useState([]);
  useEffect(() => { const n = sessionStorage.getItem("hv_checklist_added"); if (n) { sessionStorage.removeItem("hv_checklist_added"); setTimeout(() => toast.success(`${n} checklist items added to your reminders`), 600); } }, []);
  const followDoc = followDocs[0];
  const setFollowDoc = (id) => setFollowDocs((q) => (id ? (q.includes(id) ? q : [...q, id]) : q.slice(1)));
  useEffect(() => {
    if (searchParams.get("verified") === "1") {
      toast.success("Email confirmed — PIN recovery and family emails will reach you.");
      const next = new URLSearchParams(searchParams); next.delete("verified"); setSearchParams(next, { replace: true });
    }
    const refiled = searchParams.get("refiled");
    if (refiled) {
      const docId = searchParams.get("doc"), prev = searchParams.get("prev");
      const undo = prev && prev !== refiled && docId ? {
        label: "Undo",
        onClick: async () => {
          try {
            const updated = await overrideCategory(docId, prev);
            setInspecting(updated);
            refresh();
            toast.success(`Back under ${getCategory(prev).label}`);
          } catch (e) { toast.error(e?.response?.data?.detail || "Could not undo"); }
        },
      } : undefined;
      if (docId) toast.success(`Moved to ${getCategory(refiled).label} — the AI learns from your choice.`, { action: undo, duration: 8000, id: "refiled-toast" });
      setSearchParams((p) => { p.delete("refiled"); p.delete("prev"); return p; }, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [category, setCategory] = useState(() => searchParams.get("category") || "all");
  const [search, setSearch] = useState(() => searchParams.get("q") || "");
  const debouncedSearch = useDebounced(search);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [manageCatOpen, setManageCatOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [pwImportOpen, setPwImportOpen] = useState(false);
  const [kitOpen, setKitOpen] = useState(false);
  const [phoneScan, setPhoneScan] = useState(false);
  const [gmailOpen, setGmailOpen] = useState(false);
  const [migrateOpen, setMigrateOpen] = useState(false);
  const [migrateSource, setMigrateSource] = useState("");
  const [migrateResumeJob, setMigrateResumeJob] = useState(null);
  const [pwExport, setPwExport] = useState(null);
  const [trashOpen, setTrashOpen] = useState(false);
  const { readOnly, isOwner, member: me } = useSecurity();
  const [inspecting, setInspecting] = useState(null);
  const [view, setView] = useState("grid");
  const [sort, setSort] = useState({ field: "uploaded_at", dir: "desc" });

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ["documents", category, debouncedSearch],
    queryFn: () => fetchDocuments({ category, search: debouncedSearch }),
  });

  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: fetchStats });

  const openDoc = useCallback((id) => {
    fetchDocument(id)
      .then(setInspecting)
      .catch((e) => logError("could not open document", e));
  }, []);

  // Open inspector from ?doc=<id> (e.g. from a reminder click)
  const qParam = searchParams.get("q");
  useEffect(() => { if (qParam != null) { setSearch(qParam); setSearchParams((prev) => { prev.delete("q"); return prev; }, { replace: true }); } }, [qParam, setSearchParams]);
  const docParam = searchParams.get("doc");
  useEffect(() => {
    if (!docParam) return;
    openDoc(docParam);
    setSearchParams(
      (prev) => {
        prev.delete("doc");
        return prev;
      },
      { replace: true }
    );
  }, [docParam, openDoc, setSearchParams]);

  // ?open=<panel>[&section=<row>] — deep links from the Guide, first-run card, Admin checkup and the phone-scan QR code
  const openParam = searchParams.get("open");
  const sectionParam = searchParams.get("section");
  useEffect(() => {
    if (!openParam) return;
    const writes = { camera: () => setCameraOpen(true), record: () => setRecordOpen(true), upload: () => setUploadOpen(true), forward: () => setForwardOpen(true), import: () => setPwImportOpen(true), kit: () => setKitOpen(true), phone: () => setPhoneScan(true), gmail: () => setGmailOpen(true), trash: () => setTrashOpen(true), migrate: () => { setMigrateSource(searchParams.get("from") || ""); setMigrateOpen(true); } };
    if (writes[openParam]) {
      if (!readOnly) writes[openParam]();
    } else {
      setTimeout(() => window.dispatchEvent(new CustomEvent("hv:open", { detail: openParam })), 150);
      if (sectionParam) setTimeout(() => window.dispatchEvent(new CustomEvent("hv:scroll", { detail: sectionParam })), 700);
    }
    setSearchParams((prev) => { prev.delete("open"); prev.delete("section"); prev.delete("from"); return prev; }, { replace: true });
  }, [openParam, sectionParam, readOnly, searchParams, setSearchParams]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["documents"] });
    qc.invalidateQueries({ queryKey: ["stats"] });
    qc.invalidateQueries({ queryKey: ["reminders"] });
    qc.invalidateQueries({ queryKey: ["owner-checklist"] });
  };

  const counts = stats?.by_category || {};
  const total = stats?.total || 0;

  const sortedDocs = useMemo(() => {
    const arr = [...documents];
    const { field, dir } = sort;
    arr.sort((a, b) => {
      let av = a[field];
      let bv = b[field];
      if (field === "confidence") {
        av = av || 0;
        bv = bv || 0;
      } else {
        av = String(av || "").toLowerCase();
        bv = String(bv || "").toLowerCase();
      }
      if (av < bv) return dir === "asc" ? -1 : 1;
      if (av > bv) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [documents, sort]);

  const handleOverride = async (id, cat) => {
    try {
      const updated = await overrideCategory(id, cat);
      toast.success(`Re-filed under ${getCategory(cat).label}`);
      setInspecting(updated);
      refresh();
    } catch (e) {
      logError(e);
      toast.error(e?.response?.data?.detail || "Could not update category");
    }
  };

  const handleDelete = async (doc) => {
    try {
      await deleteDocument(doc.id);
      toast.success("Removed from vault", { description: doc.title });
      if (inspecting?.id === doc.id) setInspecting(null);
      refresh();
    } catch (e) {
      logError(e);
      toast.error(e?.response?.data?.detail || "Could not delete document");
    }
  };

  const heading = useMemo(
    () => (category === "all" ? tr("All Documents") : tr(getCategory(category).label)),
    [category]
  );

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-center" richColors style={{ pointerEvents: "auto" }} />
      <Navbar
        search={search}
        setSearch={setSearch}
        onUpload={() => setUploadOpen(true)}
        onForward={() => setForwardOpen(true)}
        onCamera={() => setCameraOpen(true)}
        onViewDocument={openDoc}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-6"
        >
          <p className="text-xs uppercase tracking-widest font-mono text-primary mb-2">
            {tr("Your calm home archive")}
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight max-w-2xl">
            {tr("Every important paper, filed and ready.")}
          </h1>
          <p className="mt-3 text-muted-foreground max-w-xl leading-relaxed">
            {tr("Upload, email, text or fax a document. HearthVault names it, pulls out what matters and files it in the right place — you approve every filing.")}
          </p>
        </motion.div>

        {!readOnly && <CapturePanel onUpload={() => setUploadOpen(true)} onCamera={() => setCameraOpen(true)} onRecord={() => setRecordOpen(true)} onForward={() => setForwardOpen(true)} phoneOpen={phoneScan} setPhoneOpen={setPhoneScan} gmailOpen={gmailOpen} setGmailOpen={setGmailOpen} migrateOpen={migrateOpen} setMigrateOpen={setMigrateOpen} migrateSource={migrateSource} migrateResumeJob={migrateResumeJob} onImported={() => { refresh(); qc.invalidateQueries({ queryKey: ["migrate-insights"] }); }} />}

        {readOnly && (
          <div
            data-testid="readonly-banner"
            className="mb-5 rounded-xl border border-border bg-muted/50 px-4 py-2.5 text-sm text-muted-foreground"
          >
            {tr("You're viewing this vault as an")} <span className="font-medium text-foreground">{tr("Executor")}</span> {tr("— read-only. You can browse documents and the estate plan, but not add, edit, or reveal passwords.")}
          </div>
        )}
        {!readOnly && isOwner && <OwnerChecklist />}
        <WelcomeCard />
        <AllowanceNudge />
        <StatsShelf stats={stats} />
        {isOwner && <VerifyEmailBanner />}
        {isOwner && <PendingReviewNudge />}
        {!readOnly && <NeedsVoiceNudge />}
        <InstallPrompt />
        {!readOnly && <GettingStarted documentCount={documents.length} onUpload={() => setUploadOpen(true)} onCamera={() => setCameraOpen(true)} onMigrate={(src) => { setMigrateSource(src || ""); setMigrateResumeJob(null); setMigrateOpen(true); }} memberName={me?.name?.split(" ")[0]} />}
        {!readOnly && <ImportInsightsCard isOwner={isOwner} onUpload={() => setUploadOpen(true)} onOpenMigrate={(jobId) => { setMigrateSource(""); setMigrateResumeJob(jobId || null); setMigrateOpen(true); }} />}
        {!readOnly && (
          <div data-testid="passwords-toolbar" className="mt-4 flex flex-wrap items-center gap-2 text-xs">
            <span className="font-mono uppercase tracking-wider text-muted-foreground mr-1">{tr("Passwords")}</span>
            <Tip label={tr("Bring logins from 1Password, Bitwarden, LastPass, Passbolt, Chrome, Apple and more — stored encrypted")}><button data-testid="import-passwords-btn" onClick={() => setPwImportOpen(true)} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 hover:border-primary hover:text-primary transition-colors">
              <KeyRound className="h-3.5 w-3.5" /> {tr("Import from password manager")}
            </button></Tip>
            <Tip label={tr("Record where your password manager's master key and recovery codes live, so a partner can get in")}><button data-testid="emergency-kit-btn" onClick={() => setKitOpen(true)} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 hover:border-primary hover:text-primary transition-colors">
              <LifeBuoy className="h-3.5 w-3.5" /> {tr("Emergency kit card")}
            </button></Tip>
            <Tip label={tr("Weak, reused, stale and no-2FA logins scored — passwords never leave the server")}><Link to="/passwords/health" data-testid="password-health-link" className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 hover:border-primary hover:text-primary transition-colors">
              <ShieldCheck className="h-3.5 w-3.5" /> {tr("Password health check")}
            </Link></Tip>
            {isOwner && <Tip label="Export your vault's logins in Bitwarden's import format"><button data-testid="bitwarden-export-btn" onClick={() => setPwExport("bitwarden")} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 hover:border-primary hover:text-primary transition-colors">
              <Send className="h-3.5 w-3.5" /> {tr("Send to Bitwarden")}
            </button></Tip>}
            {isOwner && <Tip label={tr("Export your vault's logins as a 1Password CSV")}><button data-testid="onepassword-export-btn" onClick={() => setPwExport("1password")} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 hover:border-primary hover:text-primary transition-colors">
              <Send className="h-3.5 w-3.5" /> {tr("Send to 1Password")}
            </button></Tip>}
            <Link to="/help" data-testid="vault-help-link" className="ml-auto inline-flex items-center gap-1.5 text-primary hover:underline"><LifeBuoy className="h-3.5 w-3.5" /> {tr("Help")}</Link>
            <Link to="/guide" data-testid="vault-guide-link" className="inline-flex items-center gap-1.5 text-primary hover:underline"><Compass className="h-3.5 w-3.5" /> {tr("What else can I do? Open the guide")}</Link>
          </div>
        )}

        <div className="mt-8">
          <RecoveryEmailNudge />
          {!readOnly && <SigningDayCard />}
          <AutofillCard readOnly={readOnly} />
          <AutofillReviewPrompt readOnly={readOnly} />
          {!readOnly && <div className="mb-6"><ReviewPrompt /></div>}
          <ExecutorModeCard />
          {isOwner && <TrialBanner />}
          <ConciergeBanner onOpenDoc={openDoc} />
          <div className="flex items-end gap-2">
            <div className="flex-1 min-w-0">
              <CategoryFilterDock
                active={category}
                setActive={setCategory}
                counts={counts}
                total={total}
              />
            </div>
            {!readOnly && (
              <button
                data-testid="manage-categories-btn"
                onClick={() => setManageCatOpen(true)}
                className="shrink-0 mb-2 flex items-center gap-1.5 rounded-full border border-dashed border-border px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              >
                <FolderPlus className="h-4 w-4" /> Categories
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-serif text-2xl font-medium">
            {heading}
            <span className="ml-2 text-base text-muted-foreground font-mono font-normal">
              ({sortedDocs.length})
            </span>
          </h2>

          <div className="flex flex-wrap items-center gap-2">
            {!readOnly && (
              <Tip label={tr("Restore a document within 30 days of deleting it")}>
                <button data-testid="trash-open-btn" onClick={() => setTrashOpen(true)} className="inline-flex items-center gap-1.5 h-9 rounded-full border border-border bg-card px-3 text-xs text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                  <Trash2 className="h-3.5 w-3.5" /> {tr("Recently deleted")}
                </button>
              </Tip>
            )}
            <Select
              value={`${sort.field}:${sort.dir}`}
              onValueChange={(v) => {
                const [field, dir] = v.split(":");
                setSort({ field, dir });
              }}
            >
              <SelectTrigger data-testid="sort-select" className="w-[150px] sm:w-[170px] bg-card h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {tr(o.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center rounded-full border border-border bg-card p-0.5">
              <button
                data-testid="view-grid-btn"
                onClick={() => setView("grid")}
                className={`h-8 w-8 grid place-items-center rounded-full transition-colors ${
                  view === "grid" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                }`}
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                data-testid="view-table-btn"
                onClick={() => setView("table")}
                className={`h-8 w-8 grid place-items-center rounded-full transition-colors ${
                  view === "table" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                }`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-4">
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {SKELETONS.map((k) => (
                <div key={k} className="h-52 rounded-2xl bg-card border border-border animate-pulse" />
              ))}
            </div>
          ) : sortedDocs.length === 0 && !search && !readOnly && documents.length === 0 ? (
            null
          ) : sortedDocs.length === 0 ? (
            <div
              data-testid="empty-state"
              className="rounded-2xl border border-dashed border-border bg-card/50 py-16 px-6 text-center"
            >
              <div className="h-16 w-16 mx-auto rounded-2xl bg-primary/10 text-primary grid place-items-center">
                <FolderOpen className="h-8 w-8" />
              </div>
              <h3 className="mt-4 font-serif text-xl" data-testid="vault-empty-title">{search ? `${tr("No matches for")} “${search}”` : tr("Nothing filed here yet")}</h3>
              <p className="mt-1.5 text-muted-foreground max-w-sm mx-auto">
                {search
                  ? tr("Try another word, or clear the search to see everything.")
                  : "Add your first document and watch it get organized instantly."}
              </p>
              {search && <Button variant="outline" onClick={() => setSearch("")} className="mt-5 rounded-full" data-testid="vault-clear-search">{tr("Clear search")}</Button>}
              {!readOnly && !search && (
                <Button
                  onClick={() => setUploadOpen(true)}
                  className="mt-5 gap-2 bg-primary hover:bg-primary/90 rounded-full"
                >
                  <UploadCloud className="h-4 w-4" /> {tr("Upload a document")}
                </Button>
              )}
            </div>
          ) : view === "grid" ? (
            <DocumentGrid documents={sortedDocs} onInspect={setInspecting} onDelete={readOnly ? null : handleDelete} />
          ) : (
            <DocumentTable
              documents={sortedDocs}
              sort={sort}
              setSort={setSort}
              onInspect={setInspecting}
              onDelete={readOnly ? null : handleDelete}
            />
          )}
        </div>
      </main>

      <UploadDropzoneModal open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={(d) => { refresh(); if (d?.id) setFollowDoc(d.id); }} />
      {followDoc && !readOnly && <UploadFollowUp key={followDoc} docId={followDoc} onClose={() => setFollowDoc(null)} />}
      <ForwardingHubModal open={forwardOpen} onClose={() => setForwardOpen(false)} onForwarded={refresh} />
      <PasswordExportDialog manager={pwExport} onClose={() => setPwExport(null)} />
      <CameraCaptureModal open={cameraOpen} onClose={() => setCameraOpen(false)} onUploaded={(d) => { refresh(); if (d?.id) setFollowDoc(d.id); }} />
      <RecordLetterModal open={recordOpen} onClose={() => setRecordOpen(false)} onUploaded={refresh} />
      <PasswordImportModal open={pwImportOpen} onClose={() => setPwImportOpen(false)} onImported={refresh} />
      <EmergencyKitModal open={kitOpen} onClose={() => setKitOpen(false)} onCreated={refresh} />
      <TrashSheet open={trashOpen} onClose={() => setTrashOpen(false)} onChanged={refresh} />
      <DocumentInspector
        doc={inspecting}
        open={!!inspecting}
        onClose={() => setInspecting(null)}
        onOverride={handleOverride}
        onUpdated={(d) => { setInspecting(d); refresh(); }}
        onDelete={readOnly ? null : handleDelete}
        readOnly={readOnly}
      />
      {!readOnly && <CategoryManager open={manageCatOpen} onClose={() => setManageCatOpen(false)} />}
      <AskVaultWidget onOpenDoc={openDoc} />
    </div>
  );
}
